/**
 * DenseErrorSummary — F-003.3 Phase 1 (IMPL-P1-004).
 * Alert riepilogo errori per sezione. Visibile solo quando visible=true.
 * role="alert" + aria-live="assertive" per annuncio screen reader immediato.
 * onJumpTo(sectionId) apre la sezione corrispondente e ne sposta il focus.
 */
import type { FieldErrors } from 'react-hook-form';
import type { ContractDraft } from '@app/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { DENSE_SECTIONS } from '../../lib/dense-sections';
import type { DenseSectionId } from '../../lib/dense-sections';

export interface DenseErrorSummaryProps {
  errors: FieldErrors<ContractDraft>;
  onJumpTo: (sectionId: DenseSectionId) => void;
  visible: boolean;
}

function getSectionsWithErrors(
  errors: FieldErrors<ContractDraft>,
): Array<{ id: DenseSectionId; title: string }> {
  return DENSE_SECTIONS.filter((section) =>
    section.fields.some((fieldPath) => {
      const parts = fieldPath.split('.');
      let cursor: unknown = errors;
      for (const p of parts) {
        if (cursor == null || typeof cursor !== 'object') return false;
        cursor = (cursor as Record<string, unknown>)[p];
      }
      return cursor != null;
    }),
  ).map((s) => ({ id: s.id, title: s.title }));
}

export function DenseErrorSummary({ errors, onJumpTo, visible }: DenseErrorSummaryProps) {
  if (!visible) return null;

  const sectionsWithErrors = getSectionsWithErrors(errors);
  if (sectionsWithErrors.length === 0) return null;

  return (
    <Alert
      id="dense-error-summary"
      variant="destructive"
      role="alert"
      aria-live="assertive"
      className="mb-4"
    >
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Ci sono campi da completare</AlertTitle>
      <AlertDescription>
        <ul className="mt-2 space-y-1 list-none p-0">
          {sectionsWithErrors.map(({ id, title }) => (
            <li key={id}>
              <button
                type="button"
                className="text-sm underline hover:no-underline focus:outline-none focus:ring-1 focus:ring-destructive"
                onClick={() => onJumpTo(id)}
              >
                {title}
              </button>
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
